import React, { useEffect, useRef, useState } from 'react';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import { ShieldCheck, Headphones, BadgeCheck, Zap } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';

const iconMap = { ShieldCheck, Headphones, BadgeCheck, Zap };

const StatItem = ({ stat, index, lang }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative p-6 sm:p-8 border-r border-b lg:border-b-0 [&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r lg:last:border-r-0 border-white/5 transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="absolute top-6 right-6 text-[10px] text-white/30 uppercase tracking-widest">
        0{index + 1}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight">
          {stat.value}
        </span>
        {stat.suffix && (
          <span className="text-xl sm:text-2xl text-[#00e0c7] font-light">{pickI18n(stat.suffix, lang)}</span>
        )}
      </div>
      <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-white/55 leading-relaxed max-w-[230px]">
        {pickI18n(stat.label, lang)}
      </p>
      <div className="absolute bottom-0 left-0 w-0 h-px bg-gradient-to-r from-[#00e0c7] to-transparent group-hover:w-full transition-all duration-700" />
    </div>
  );
};

const ImpactNumbers = () => {
  const { content } = useSite();
  const { t, lang } = useLang();
  const impactStats = content?.impactStats || [];
  const guarantees = content?.guarantees || [];
  const sec = content?.impactSection || {};
  return (
    <section className="relative bg-[#0d1b2a] py-16 sm:py-24 overflow-hidden">
      <AnimatedBackground variant="aurora" intensity={0.3} />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="mb-12 sm:mb-16 flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-4">
              · {pickI18n(sec.eyebrow, lang) || t('sections.impactNumbers')}
            </div>
            <h2 className="font-display text-white text-2xl sm:text-3xl lg:text-5xl font-light tracking-tight max-w-2xl" style={{ lineHeight: 1.25 }}>
              {pickI18n(sec.title, lang)}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 border-y border-white/5">
          {impactStats.map((s, i) => (
            <StatItem key={i} stat={s} index={i} lang={lang} />
          ))}
        </div>

        {/* Guarantees */}
        <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {guarantees.map((g, i) => {
            const Icon = iconMap[g.icon];
            return (
              <div
                key={i}
                className="group p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-[#00e0c7]/30 hover:bg-white/[0.04] transition-all duration-500"
              >
                <div className="w-11 h-11 rounded-xl bg-[#00e0c7]/10 border border-[#00e0c7]/20 flex items-center justify-center mb-5 group-hover:bg-[#00e0c7]/15 transition-colors">
                  <Icon className="w-5 h-5 text-[#00e0c7]" />
                </div>
                <h4 className="text-white font-medium text-[15px] mb-2">{pickI18n(g.title, lang)}</h4>
                <p className="text-white/50 text-[13px] leading-relaxed">{pickI18n(g.text, lang)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ImpactNumbers;
